<template>
  <div id="home">
    <BannerGallery />
    <div class="relative sm:hidden">
      <TeamEventRequestButton />
    </div>
    <div v-if="lastViewedEvents?.length > 0" class="section-y">
      <ProductShowcase
        data-test-id="last-viewed-events-section"
        :loading="loading"
        :products="lastViewedEvents"
        is-slider
        image-loading="lazy"
        :gtm-item-list-id="GtmItemListId.GeneralPageLastViewedEvents"
        :tags="[]"
        title-h-class="section-title"
        :title="t('home.lastViewedEvents')"
      />
    </div>

    <template
      v-for="(section, index) in events"
      :key="'homepage-events-' + index"
    >
      <div class="section-y">
        <ProductShowcase
          :loading="loading"
          v-bind="section"
          is-slider
          image-loading="lazy"
          :tags="[]"
          title-h-class="section-title"
        />
      </div>

      <template v-if="index === 'promotions'">
        <CardsSection
          v-if="cardsSectionContent"
          :content="cardsSectionContent"
        />

        <ProductGiftcardEventsShowcase
          is-slider
          :title="t('home.inspirationForYou')"
          class="section-y"
        />
      </template>
    </template>

    <Grid
      v-if="gridSectionContent"
      :content="gridSectionContent"
      class="-mx-4"
    />

    <Carousel
      v-if="carousel"
      class="overflow-visible! w-full! -mx-2"
      :content="carousel"
    />
  </div>
</template>
<script setup lang="ts">
import organizationSchema from '../../constants/organizationSchema'
import { useHomepageSearchEngine } from '~/composables/useHomepageSearchEngine'
import { useLastViewedEvents } from '~/composables/useLastViewedEvents'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { getHomePageStrapiURL } from '~/utils/strapiURLhelpers'

const { t, locale } = useI18n()
const currentLocale: string = locale.value
const { client: insightsClient } = useAlgoliaSearchInsights()

const { data: homePageData } = await useStrapiSingleType(
  getHomePageStrapiURL(locale.value)
)

provide('homePageGalleryData', homePageData)

const cardsSectionContent = computed(
  () => homePageData.value?.data?.cardSection
)

const gridSectionContent = computed(() => homePageData.value?.data?.gridSection)

const carousel = computed(() => homePageData.value?.data?.carousel)

const { algoliaResults, pending: loading } = await useHomepageSearchEngine()

const { lastViewedEvents } = useLastViewedEvents(true)

const filteredGiftIdeas = computed(() => {
  if (!algoliaResults?.value?.results) return []

  return algoliaResults?.value?.results?.[2]?.hits
    .filter(
      (event) =>
        !events?.value?.topEvents?.products
          .map((topEvent) => topEvent.objectID)
          .includes(event.objectID)
    )
    .slice(0, 8)
})

const events = computed(() => {
  const results = algoliaResults?.value?.results
  if (!results) return {}

  return {
    topEvents: {
      title: t('home.browseTopEvents'),
      buttonLink: '/search/',
      'data-test-id': 'top-events-section',
      gtmItemListId: GtmItemListId.GeneralPageTopEvents,
      algoliaInsightsClient: insightsClient,
      algoliaInsightsLabel: 'Top Events - Homepage',
      algoliaIndexName: getTopEventListIndexName(locale.value),
      products: results[0]?.hits,
      queryId: results[0]?.queryID,
    },
    promotions: {
      title: t('home.promotionEventsTitle'),
      buttonLink: '/search/?hasPromotion=true',
      'data-test-id': 'promotion-events-section',
      algoliaInsightsLabel: 'Promotions - Homepage',
      gtmItemListId: GtmItemListId.GeneralPagePromotions,
      algoliaIndexName: getEventListIndexName(locale.value),
      products: results[3]?.hits,
      queryId: results[3]?.queryID,
    },
    kits: {
      title: t('home.kitsTitle'),
      buttonLink: '/kits/',
      'data-test-id': 'kit-events-section',
      gtmItemListId: GtmItemListId.GeneralPageKits,
      algoliaInsightsClient: insightsClient,
      algoliaInsightsLabel: 'Kits - Homepage',
      algoliaIndexName: getBoxesIndexName(locale.value),
      products: results[4]?.hits,
      queryId: results[4]?.queryID,
    },
    topGiftIdeasEvents: {
      title: t('home.recommendedForYou'),
      'data-test-id': 'recommended-events-section',
      gtmItemListId: GtmItemListId.GeneralPageTopGiftIdeas,
      algoliaInsightsClient: insightsClient,
      algoliaInsightsLabel: 'Gift Event Ideas - Homepage',
      algoliaIndexName: getTopGiftIdeasIndexName(locale.value),
      products: filteredGiftIdeas.value,
      queryId: results[2]?.queryID,
    },
    newEvents: {
      title: t('home.newEventsForYou'),
      buttonLink: '/search/?orderBy=newest',
      'data-test-id': 'new-events-section',
      gtmItemListId: GtmItemListId.GeneralPageNewEvents,
      algoliaInsightsClient: insightsClient,
      algoliaInsightsLabel: 'New Events - Homepage',
      algoliaIndexName: getEventListSortByDateDescIndexName(locale.value),
      products: results[1]?.hits,
      queryId: results[1]?.queryID,
    },
  }
})

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: organizationSchema[currentLocale],
    },
  ],
})

definePageMeta({
  preloaderType: 'home',
  contentGroup: 'homepage',
})
</script>
